import React, { Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { CircularProgress } from '@material-ui/core'

import { constants } from '../../../App.constants'
import { currentPricing } from '../../docs/memberBenefits'
import { addOnRenewalEmbeddedUrl } from '../../../store/actions/addOnRenewalActions'
import { bridgeReturnUrl, planDisplay } from './addOnRenewalHelpers'

/**
 * Step 3: DocuSign embedded signing, the same mechanism as ContractSign. The
 * server chooses the add-on template and fills the member's details from the
 * renewal; the iframe returns through /docusign-bridge, which posts
 * DOCUSIGN_DONE / DOCUSIGN_EVENT with this page's nonce and sid. The page
 * (AddOnRenewal) listens for that message.
 */
const SigningStep = ({ loginToken, renewalId, nonce, sid, plan, notice, placeOfBirth, onDetailsIncomplete }) => {
    const dispatch = useDispatch()
    const [signUrl, setSignUrl] = useState(null)
    const [loading, setLoading] = useState(false)
    const [iframeLoaded, setIframeLoaded] = useState(false)
    const [errorMsg, setErrorMsg] = useState(null)

    const beginSigning = () => {
        setLoading(true)
        setErrorMsg(null)

        const request = {
            renewalId,
            orgId: constants.selectedOrg,
            returnUrl: bridgeReturnUrl({ nonce, sid, renewalId }),
            signer1: { roleName: 'Member', clientUserId: `addon-${renewalId}` },
            // Prices and place of birth (contract-only, from the details
            // step); the server sets every member and term field.
            prefill: {
                membership_field: planDisplay(plan).name,
                pob_field: placeOfBirth || undefined,
                initiation_fee_golf: currentPricing.Golf.initiation_fee,
                initiation_fee_social: currentPricing.Social.initiation_fee,
                member_dues_golf: currentPricing.Golf.amount,
                member_dues_social: currentPricing.Social.amount,
                golf_add_on: currentPricing.Golf_Addon.amount,
                social_add_on: currentPricing.Social_Addon.amount
            }
        }

        dispatch(addOnRenewalEmbeddedUrl(loginToken, request, (err, data) => {
            setLoading(false)
            if (err && err.code === 'details_incomplete' && onDetailsIncomplete) {
                onDetailsIncomplete()
                return
            }
            if (err || !data || !data.url) {
                setErrorMsg((err && err.message) || 'Failed to start signing')
                return
            }
            setSignUrl(data.url)
        }))
    }

    return <Grid container direction="column" spacing={2}>
        {!signUrl ? <Fragment>
            <Grid item>
                <div style={{ padding: '14px 16px', borderRadius: 10, background: '#f7f8fa', border: '1px solid #e3e6eb', marginBottom: 16 }}>
                    <Typography style={{ fontWeight: 600 }}>Sign your {planDisplay(plan).name} membership agreement</Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                        The agreement opens here in DocuSign with your details filled in. It takes about a minute; your renewal is completed as soon as you sign.
                    </Typography>
                </div>
            </Grid>
            <Grid item>
                <Button type="button" onClick={beginSigning} fullWidth disabled={loading} variant="contained" size="large" color="primary">
                    {loading ? <span>Starting, please wait...<CircularProgress size={'14px'} /></span> : 'Review and Sign Contract'}
                </Button>
                {(errorMsg || notice) && <div style={{ color: '#a83232' }}>{errorMsg || notice}</div>}
                <p style={{ fontSize: 12, color: '#646b78' }}>
                    Please complete the e-sign process to renew your add-on membership.
                </p>
            </Grid>
        </Fragment> : <Grid item>
            {!iframeLoaded && <span>Prefilling details..<CircularProgress size={'14px'} /></span>}
            <iframe
                onLoad={() => setIframeLoaded(true)}
                title="DocuSign Signing"
                src={signUrl}
                style={{ width: '100%', height: '90vh', border: 0 }}
                allow="clipboard-read; clipboard-write"
            />
        </Grid>}
    </Grid>
}

export default SigningStep
